import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { formatCurrencyINR } from '../../../utils/currency';
import { generateId } from '../../../schemas/dataSchemas';
import { useUserData } from '../../../hooks/useUserData';

const InvestmentModal = ({ isOpen, onClose, onSave, editingInvestment = null }) => {
  const { userData } = useUserData();
  const currentYear = new Date().getFullYear();
  const retirementYear = userData?.setup?.retirementYear || currentYear + 20;

  const [formData, setFormData] = useState({
    name: editingInvestment?.name || '',
    type: editingInvestment?.type || 'Equity',
    frequency: editingInvestment?.frequency || 'monthly',
    amount: editingInvestment?.amount || '',
    startYear: editingInvestment?.startYear || currentYear,
    endYear: editingInvestment?.endYear || retirementYear,
    expectedReturn: editingInvestment?.expectedReturn || '',
    autoReinvest: editingInvestment?.autoReinvest !== undefined ? editingInvestment.autoReinvest : true,
    priority: editingInvestment?.priority || '',
    notes: editingInvestment?.notes || ''
  });

  const [errors, setErrors] = useState({});

  // Update form data when editingInvestment changes
  useEffect(() => {
    if (editingInvestment) {
      setFormData({
        name: editingInvestment.name || '',
        type: editingInvestment.type || 'Equity',
        frequency: editingInvestment.frequency || 'monthly',
        amount: editingInvestment.amount || '',
        startYear: editingInvestment.startYear || currentYear,
        endYear: editingInvestment.endYear || retirementYear,
        expectedReturn: editingInvestment.expectedReturn || '',
        autoReinvest: editingInvestment.autoReinvest !== undefined ? editingInvestment.autoReinvest : true,
        priority: editingInvestment.priority || '',
        notes: editingInvestment.notes || ''
      });
    } else {
      // Reset form when not editing
      setFormData({
        name: '',
        type: 'Equity',
        frequency: 'monthly',
        amount: '',
        startYear: currentYear,
        endYear: retirementYear,
        expectedReturn: '',
        autoReinvest: true,
        priority: '',
        notes: ''
      });
    }
    setErrors({});
  }, [editingInvestment, currentYear, retirementYear]);

  const assetClassOptions = [
    { value: 'Equity', label: 'Equity' },
    { value: 'Debt', label: 'Debt' },
    { value: 'Gold', label: 'Gold' },
    { value: 'FD', label: 'Fixed Deposits (FD)' },
    { value: 'Real Estate', label: 'Real Estate' },
    { value: 'Mutual Funds', label: 'Mutual Funds' },
    { value: 'PPF', label: 'PPF' },
    { value: 'Cash', label: 'Cash' },
    { value: 'Other', label: 'Other' }
  ];

  const frequencyOptions = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'annually', label: 'Annual' },
    { value: 'one-time', label: 'One-time' }
  ];

  const priorityOptions = [
    { value: '', label: 'None (Optional)' },
    { value: '1', label: '1 - Highest Priority' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5 - Lowest Priority' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Investment name is required';
    }

    if (!formData.type || formData.type === '') {
      newErrors.type = 'Asset class is required';
    }

    if (!formData.frequency || formData.frequency === '') {
      newErrors.frequency = 'Frequency is required';
    }

    if (!formData.amount || formData.amount === '' || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Investment amount is required and must be greater than 0';
    }
    
    const amount = parseFloat(formData.amount);
    if (formData.amount && (isNaN(amount) || amount <= 0)) {
      newErrors.amount = 'Investment amount must be a valid positive number';
    }

    if (!formData.startYear || formData.startYear === '' || parseInt(formData.startYear) < currentYear) {
      newErrors.startYear = `Start year is required and must be ${currentYear} or later`;
    }

    if (!formData.endYear || formData.endYear === '' || parseInt(formData.endYear) < parseInt(formData.startYear)) {
      newErrors.endYear = 'End year must be after start year';
    }

    if (formData.expectedReturn === '' || formData.expectedReturn === null || formData.expectedReturn === undefined) {
      newErrors.expectedReturn = 'Expected return is required';
    }
    
    const expectedReturn = parseFloat(formData.expectedReturn);
    if (formData.expectedReturn !== '' && (isNaN(expectedReturn) || expectedReturn < 0 || expectedReturn > 20)) {
      newErrors.expectedReturn = 'Expected return must be between 0% and 20%';
    }

    if (formData.priority && (parseInt(formData.priority) < 1 || parseInt(formData.priority) > 5)) {
      newErrors.priority = 'Priority must be between 1 and 5';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateAnnualAmount = () => {
    if (!formData.amount || !formData.frequency) return 0;
    
    const amount = parseFloat(formData.amount) || 0;
    const freq = formData.frequency;
    
    if (freq === 'monthly') return amount * 12;
    if (freq === 'quarterly') return amount * 4;
    if (freq === 'annually') return amount;
    if (freq === 'one-time') return amount;
    return 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const investmentData = {
      id: editingInvestment?.id || generateId(),
      name: formData.name.trim(),
      type: formData.type,
      frequency: formData.frequency,
      amount: parseFloat(formData.amount),
      startYear: parseInt(formData.startYear),
      endYear: parseInt(formData.endYear),
      expectedReturn: parseFloat(formData.expectedReturn),
      autoReinvest: formData.autoReinvest,
      priority: formData.priority ? parseInt(formData.priority) : undefined,
      isActive: true,
      notes: formData.notes || ''
    };

    onSave(investmentData);
    onClose();
  };

  const handleClose = () => {
    setFormData({
      name: '',
      type: 'Equity',
      frequency: 'monthly',
      amount: '',
      startYear: currentYear,
      endYear: retirementYear,
      expectedReturn: '',
      autoReinvest: true,
      priority: '',
      notes: ''
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="TrendingUp" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {editingInvestment ? 'Edit Investment' : 'Add New Investment'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {editingInvestment ? 'Update investment details' : 'Enter investment information'}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto max-h-[60vh]">
          {/* Validation Error Summary */}
          {Object.keys(errors).length > 0 && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-destructive mb-1">
                    Please fix the following errors:
                  </h3>
                  <ul className="text-sm text-destructive space-y-1">
                    {Object.entries(errors).map(([field, error]) => (
                      <li key={field} className="flex items-start gap-2">
                        <span className="text-destructive">•</span>
                        <span>{error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Investment Name"
              placeholder="e.g., SIP – Equity Fund"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={errors.name}
              required
            />

            <Select
              label="Type / Asset Class"
              options={assetClassOptions}
              value={formData.type}
              onChange={(value) => handleInputChange('type', value)}
              error={errors.type}
              required
            />

            <Select
              label="Frequency"
              options={frequencyOptions}
              value={formData.frequency}
              onChange={(value) => handleInputChange('frequency', value)}
              error={errors.frequency}
              required
            />

            <Input
              label={`Investment Amount (₹)${formData.frequency === 'monthly' ? '/month' : formData.frequency === 'quarterly' ? '/quarter' : formData.frequency === 'annually' ? '/year' : ''}`}
              type="number"
              placeholder="25000"
              value={formData.amount}
              onChange={(e) => handleInputChange('amount', e.target.value)}
              error={errors.amount}
              description={formData.frequency && formData.amount ? `Annual: ${formatCurrencyINR(calculateAnnualAmount())}` : ''}
              required
            />

            <Input
              label="Start Year"
              type="number"
              placeholder={currentYear.toString()}
              value={formData.startYear}
              onChange={(e) => handleInputChange('startYear', e.target.value)}
              error={errors.startYear}
              required
            />

            <Input
              label="End Year"
              type="number"
              placeholder={retirementYear.toString()}
              value={formData.endYear}
              onChange={(e) => handleInputChange('endYear', e.target.value)}
              error={errors.endYear}
              description="Contributions stop at this year, but compounding continues"
              required
            />

            <Input
              label="Expected Return (% p.a.)"
              type="number"
              step="0.1"
              placeholder="10"
              value={formData.expectedReturn}
              onChange={(e) => handleInputChange('expectedReturn', e.target.value)}
              error={errors.expectedReturn}
              description="Annualized growth assumption"
              required
            />

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Auto Reinvest? (Y/N)
              </label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={formData.autoReinvest === true}
                    onChange={() => handleInputChange('autoReinvest', true)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-foreground">Yes (Returns reinvested in same asset)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={formData.autoReinvest === false}
                    onChange={() => handleInputChange('autoReinvest', false)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-foreground">No (Returns go to Cash)</span>
                </label>
              </div>
            </div>

            <Select
              label="Priority / Allocation % (Optional)"
              options={priorityOptions}
              value={formData.priority}
              onChange={(value) => handleInputChange('priority', value)}
              error={errors.priority}
              description="Execution order when surplus is insufficient (1 = highest priority)"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Notes
            </label>
            <textarea
              placeholder="Additional notes about this investment..."
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          {/* Investment Preview */}
          <div className="bg-muted p-4 rounded-lg">
            <h3 className="font-medium text-foreground mb-3">Investment Preview</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Annual Investment:</span>
                <span className="ml-2 font-medium">{formatCurrencyINR(calculateAnnualAmount())}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Duration:</span>
                <span className="ml-2 font-medium">
                  {formData.startYear && formData.endYear 
                    ? `${parseInt(formData.endYear) - parseInt(formData.startYear)} years`
                    : 'N/A'}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Auto Reinvest:</span>
                <span className="ml-2 font-medium">
                  {formData.autoReinvest ? 'Yes' : 'No'}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Priority:</span>
                <span className="ml-2 font-medium">
                  {formData.priority ? formData.priority : 'None'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            {editingInvestment ? 'Update Investment' : 'Add Investment'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InvestmentModal;



